import { useController, type FieldValues } from 'react-hook-form';
import { Form } from 'semantic-ui-react';

import FileUploader from '@components/file-uploader';

import { FieldError } from './field-error';
import type { BaseFieldProps } from './field.types';

export type FieldFileProps<T extends FieldValues = FieldValues> =
  BaseFieldProps<T>;

/**
 * File upload field wired to React Hook Form via `useController`.
 *
 * Wraps `FileUploader` (drag-and-drop + file picker) inside a `Form.Field`,
 * syncing the selected `File` object to form state and displaying validation
 * errors.
 *
 * @see FileUploader
 */
export const FieldFile = <T extends FieldValues>({
  className,
  control,
  label,
  name,
  required,
  width,
}: FieldFileProps<T>) => {
  const { field, fieldState } = useController({ control, name });
  const id = name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;

  return (
    <Form.Field
      className={className}
      error={hasError}
      required={required}
      width={width}
    >
      {label && <label htmlFor="input-file-upload">{label}</label>}

      <FileUploader
        onSelectFile={file => field.onChange(file ?? null)}
        selectedFile={field.value ?? undefined}
      />

      <FieldError id={errorId} message={fieldState.error?.message} />
    </Form.Field>
  );
};
